import React from 'react';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import '../../styles/css/MessageInput.css';

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    marginBottom: '20px',
    marginTop: '20px'
  },
  inputField: {
    flex: 1,
    padding: '12px',
    borderRadius: '20px',
    border: '1px solid #ccc',
    fontSize: '16px',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: '#3f51b5',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '20px',
    },
  },
  actionButton: {
    color: 'white',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: (props) => (props.disabled ? 'not-allowed' : 'pointer'),
    opacity: (props) => (props.disabled ? 0.5 : 1),
    pointerEvents: (props) => (props.disabled ? 'none' : 'auto'),
  },
  sendButton: {
    backgroundColor: '#3f51b5',
  },
  stopButton: {
    backgroundColor: '#e53935',
  },
}));

const MessageInput = ({ value, onChange, onSend, onStop, language, sending }) => {
  // Button stays interactive while sending so it can act as a stop control.
  const classes = useStyles({ disabled: !language });

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!sending) onSend();
    }
  };

  // Keyboard activation for the div-based action button (Enter/Space), so the
  // stop control is reachable and operable without a mouse.
  const handleButtonKeyDown = (event) => {
    if (!language) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (sending) onStop(); else onSend();
    }
  };

  const getPlaceholderText = () => {
    const placeholders = {
      ru: "Введите ваше сообщение",
      uk: "Введіть ваше повідомлення", // 'ua' from profile maps to 'uk' here
      ua: "Введіть ваше повідомлення", // Also support 'ua' directly
      en: "Type your message",
    };
    // Map 'ua' to 'uk' for compatibility, default to 'en'
    const langKey = language === 'ua' ? 'uk' : (language || 'en');
    return placeholders[langKey] || placeholders.en;
  };

  const langKey = language === 'ua' ? 'uk' : (language || 'en');
  const buttonLabels = {
    ru: { send: 'Отправить', stop: 'Остановить' },
    uk: { send: 'Надіслати', stop: 'Зупинити' },
    en: { send: 'Send', stop: 'Stop' },
  };
  const labels = buttonLabels[langKey] || buttonLabels.en;

  // send-message-* classes are stable hooks for host-page CSS
  return (
    <div className={`${classes.inputContainer} send-message-container`}>
      <TextField
        id="chat_input_field"
        value={value}
        onChange={onChange}
        variant="outlined"
        fullWidth
        multiline
        maxRows={5}
        placeholder={getPlaceholderText()}
        className={classes.inputField}
        onKeyDown={handleKeyDown}
        disabled={!language}
      />
      <div
        className={`${classes.actionButton} send-message-button ${sending ? classes.stopButton : classes.sendButton}`}
        onClick={sending ? onStop : onSend}
        onKeyDown={handleButtonKeyDown}
        role="button"
        tabIndex={language ? 0 : -1}
        aria-label={sending ? labels.stop : labels.send}
        aria-disabled={!language}
        title={sending ? labels.stop : labels.send}
      >
        {sending ? (
          <span className="message-input-stop" />
        ) : (
          <span className="message-input-arrow">&#8594;</span>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
